import { FC, ReactNode } from "react";
import { useSelector } from "react-redux";

import { DesignToken, Text } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { SettingsTableColumn as RawColumn, SettingsTableRow as Row } from "components/settings-table";
import { DEFAULT_ADDRESS_ID, UNSET_DISPLAY_NAME_KEY } from "constants/settings/replyAddresses";
import { TeamsPreview } from "routes/settings/components/TeamsPreview";
import { selectLicenseId } from "store/entities/license";
import { IReplyAddress } from "store/entities/reply-addresses/interfaces";
import { ITeam } from "store/entities/teams";
import { withVar } from "themes/helpers";

import { ShowDeleteModal, ShowEditModal, ShowPreviewModal } from "../interfaces";

import { ReplyAddressActionMenu } from "./ReplyAddressActionMenu";
import { StatusBadge } from "./StatusBadge";

const Column: FC<{
  children: ReactNode;
}> = ({ children }) => <RawColumn paddingDesktop="1em 1.5em">{children}</RawColumn>;

interface IReplyAddressEntryProps {
  fromName: string | null;
  replyAddress: IReplyAddress;
  assignedTeams: ITeam[];
  showPreviewModal: ShowPreviewModal;
  showEditModal: ShowEditModal;
  showDeleteModal: ShowDeleteModal;
}

export const ReplyAddressEntry: FC<IReplyAddressEntryProps> = ({
  fromName,
  replyAddress,
  assignedTeams,
  showEditModal,
  showDeleteModal,
  showPreviewModal,
}) => {
  const { email, verified } = replyAddress;

  return (
    <Row>
      <Column>
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>{fromName ?? "Display name not set"}</Text>
        <Text>{email}</Text>
      </Column>
      <Column>
        <TeamsPreview teams={assignedTeams} />
      </Column>
      <Column>
        <Flex justifyContent="space-between">
          <StatusBadge verified={verified} />
          <ReplyAddressActionMenu
            replyAddress={replyAddress}
            fromName={fromName}
            showPreviewModal={showPreviewModal}
            showEditModal={showEditModal}
            showDeleteModal={showDeleteModal}
          />
        </Flex>
      </Column>
    </Row>
  );
};

interface IDefaultReplyAddressEntryProps {
  fromName: string;
  assignedTeams: ITeam[];
  showPreviewModal: ShowPreviewModal;
  showEditModal: ShowEditModal;
  showDeleteModal: ShowDeleteModal;
}

export const DefaultReplyAddressEntry: FC<IDefaultReplyAddressEntryProps> = ({
  fromName,
  assignedTeams,
  showPreviewModal,
  showEditModal,
  showDeleteModal,
}) => {
  const licenseID = useSelector(selectLicenseId);
  const dummyReplyAddress: IReplyAddress = {
    id: DEFAULT_ADDRESS_ID,
    createdBy: "",
    createdByType: "system",
    updatedAt: new Date(),
    updatedBy: "",
    createdAt: new Date(),
    email: `${licenseID}@tickets.helpdesk.com`,
    emailDomainID: "",
    licenseID,
    prefix: `${licenseID}`,
    verified: true,
  };

  return (
    <ReplyAddressEntry
      fromName={fromName === UNSET_DISPLAY_NAME_KEY ? null : fromName}
      replyAddress={dummyReplyAddress}
      assignedTeams={assignedTeams}
      showPreviewModal={showPreviewModal}
      showEditModal={showEditModal}
      showDeleteModal={showDeleteModal}
    />
  );
};
